import React from 'react';
import { Trash2 } from 'lucide-react';
import { Project } from '../../../../types';
import { cn } from '../../../../lib/utils';
import { MIN_DAILY_HOURS } from '../../../../lib/timeEntryHelpers';
import { Button } from '../../../../components/ui/atoms/button';
import { Input } from '../../../../components/ui/atoms/input';
import { Textarea } from '../../../../components/ui/atoms/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../../components/ui/molecules/select';
import { SearchableSelect } from '../../../../components/ui/molecules/searchable-select';
import { DayCell, EntryMode, EntryRow } from '../interface';
import { DAY_ABBR, isWeekend, formatDayDate, getCell, effectiveHours } from '../utils';

export function TimesheetTable({
  weekDates, mode, rows, projects, dayTotals, totalHours,
  onSetRowProject, onSetRowPhase, onSetCell, onRemoveRow,
}: {
  weekDates: string[];
  mode: EntryMode;
  rows: EntryRow[];
  projects: Project[];
  dayTotals: Record<string, number>;
  totalHours: number;
  onSetRowProject: (rowId: string, projectId: string) => void;
  onSetRowPhase: (rowId: string, phaseId: string) => void;
  onSetCell: (rowId: string, date: string, field: keyof DayCell, value: string) => void;
  onRemoveRow: (rowId: string) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className={cn('w-full border-collapse text-sm', mode === 'quick' ? 'min-w-[980px]' : 'min-w-[1200px]')}>
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="w-[190px] px-4 py-2.5 text-left font-medium text-muted-foreground">Project</th>
            {weekDates.map((date, i) => (
              <th
                key={date}
                className={cn(
                  mode === 'quick' ? 'min-w-[110px]' : 'min-w-[160px]',
                  'px-2 py-2.5 text-center font-medium',
                  isWeekend(i) && 'bg-muted/80 text-muted-foreground'
                )}
              >
                <div>{DAY_ABBR[i]}</div>
                <div className="text-xs font-normal text-muted-foreground">{formatDayDate(date)}</div>
                {dayTotals[date] > 0 && (
                  <div className={cn('text-xs font-normal mt-0.5', dayTotals[date] < MIN_DAILY_HOURS ? 'text-amber-600 font-medium' : 'text-muted-foreground')}>
                    {dayTotals[date]}h{dayTotals[date] < MIN_DAILY_HOURS ? ' ⚠' : ''}
                  </div>
                )}
              </th>
            ))}
            <th className="w-10 px-1" />
          </tr>
        </thead>

        <tbody>
          {rows.map((row, rowIdx) => {
            const project = projects.find((p) => p.id === row.projectId);
            const phases = project?.type?.phases ?? [];
            const rowTotal = weekDates.reduce((s, d) => s + effectiveHours(getCell(row, d), mode), 0);

            return (
              <React.Fragment key={row.rowId}>
                {/* Project / phase header row */}
                <tr className={cn('border-t', rowIdx > 0 ? 'border-t-2 border-border' : '')}>
                  <td colSpan={9} className="bg-muted/20 px-4 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <SearchableSelect
                        value={row.projectId}
                        onValueChange={(v) => onSetRowProject(row.rowId, v)}
                        options={projects.map((p) => ({ value: p.id, label: p.name }))}
                        placeholder="Select project…"
                        searchPlaceholder="Search projects..."
                        emptyMessage={projects.length === 0 ? 'No projects assigned' : undefined}
                        className="h-8 w-48"
                      />

                      {row.projectId && (
                        phases.length > 0 ? (
                          <Select value={row.phaseId} onValueChange={(v) => onSetRowPhase(row.rowId, v)}>
                            <SelectTrigger className="h-8 w-40 text-sm">
                              <SelectValue placeholder="Select phase…" />
                            </SelectTrigger>
                            <SelectContent>
                              {phases.map((ph) => (
                                <SelectItem key={ph.id} value={ph.id}>{ph.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-xs text-muted-foreground">No phases configured</span>
                        )
                      )}

                      {project?.billable && (
                        <span className="text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded px-1.5 py-0.5">
                          Billable
                        </span>
                      )}

                      {rowTotal > 0 && (
                        <span className="ml-1 text-xs text-muted-foreground">{rowTotal}h total</span>
                      )}

                      {totalHours > 0 && (
                        <div className="ml-auto text-sm font-medium">
                          {totalHours}h entered this week
                        </div>
                      )}

                      {rows.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn('h-7 w-7 text-destructive hover:text-destructive', totalHours > 0 ? 'ml-2' : 'ml-auto')}
                          onClick={() => onRemoveRow(row.rowId)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>

                {/* ── Hours row (Quick mode) or From/To rows (Range mode) ── */}
                {mode === 'quick' ? (
                  <tr>
                    <td className="px-4 py-2 font-medium text-muted-foreground">Hours</td>
                    {weekDates.map((date, i) => {
                      const cell = getCell(row, date);
                      const disabled = isWeekend(i);
                      return (
                        <td key={date} className={cn('px-2 py-2', isWeekend(i) && 'bg-muted/20')}>
                          <Input
                            type="number" step="0.5" min="0" max="12" placeholder="—"
                            value={cell.hours}
                            onChange={(e) => onSetCell(row.rowId, date, 'hours', e.target.value)}
                            disabled={disabled}
                            className="h-8 text-center text-sm placeholder:text-center pl-6"
                          />
                        </td>
                      );
                    })}
                    <td />
                  </tr>
                ) : (
                  <>
                    <tr>
                      <td className="px-4 py-2 font-medium text-muted-foreground">From</td>
                      {weekDates.map((date, i) => {
                        const cell = getCell(row, date);
                          const disabled = isWeekend(i);
                        return (
                          <td key={date} className={cn('px-2 py-2', isWeekend(i) && 'bg-muted/20')}>
                            <Input
                              type="time"
                              value={cell.fromHour}
                              onChange={(e) => onSetCell(row.rowId, date, 'fromHour', e.target.value)}
                              disabled={disabled}
                              className="h-8 text-sm text-center pl-8 pr-8 [appearance:auto] [&::-webkit-datetime-edit]:w-full [&::-webkit-datetime-edit]:text-center [&::-webkit-datetime-edit-fields-wrapper]:w-full [&::-webkit-datetime-edit-fields-wrapper]:text-center"
                            />
                          </td>
                        );
                      })}
                      <td />
                    </tr>
                    <tr>
                      <td className="px-4 py-2 font-medium text-muted-foreground">To</td>
                      {weekDates.map((date, i) => {
                        const cell = getCell(row, date);
                        const h = effectiveHours(cell, mode);
                          const disabled = isWeekend(i);
                        return (
                          <td key={date} className={cn('px-2 py-2', isWeekend(i) && 'bg-muted/20')}>
                            <Input
                              type="time"
                              value={cell.toHour}
                              onChange={(e) => onSetCell(row.rowId, date, 'toHour', e.target.value)}
                              disabled={disabled}
                              className="h-8 text-sm text-center pl-8 pr-8 [appearance:auto] [&::-webkit-datetime-edit]:w-full [&::-webkit-datetime-edit]:text-center [&::-webkit-datetime-edit-fields-wrapper]:w-full [&::-webkit-datetime-edit-fields-wrapper]:text-center"
                            />
                            {h > 0 && <div className="text-[10px] text-muted-foreground text-center mt-0.5">{h}h</div>}
                          </td>
                        );
                      })}
                      <td />
                    </tr>
                  </>
                )}


                {/* Notes row */}
                <tr>
                  <td className="px-4 py-2 font-medium text-muted-foreground">Notes</td>
                  {weekDates.map((date, i) => {
                    const cell = getCell(row, date);
                    const disabled = isWeekend(i);
                    return (
                      <td key={date} className={cn('px-2 py-2', isWeekend(i) && 'bg-muted/20')}>
                        <Textarea
                          placeholder="Describe what have you worked on (Required)"
                          value={cell.notes}
                          onChange={(e) => onSetCell(row.rowId, date, 'notes', e.target.value)}
                          disabled={disabled}
                          className="min-h-[72px] text-xs resize-y"
                        />
                      </td>
                    );
                  })}
                  <td />
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
